import type { Operation } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { logger, LoggerEvents } from '@salescore/frontend-common'
import { isNotNullish } from 'effect/Predicate'

import { notifyBugsnag } from '../bugsnag'
import { getRequestId } from './fetchRequestId'

type ErrorHandler = Parameters<typeof onError>[0]
type argument = Parameters<ErrorHandler>[0]
// eslint-disable-next-line sonarjs/deprecation
type GraphQLErrors = Exclude<argument['graphQLErrors'], undefined>

class ApolloClientNetworkError extends Error {
  public constructor(message: string, options: { cause: unknown }) {
    super(message, options)
    this.name = 'ApolloClientNetworkError'
  }
}

class ApolloClientBadUserInputError extends Error {
  // eslint-disable-next-line unicorn/custom-error-definition
  public constructor(message: string) {
    super(message)
    this.name = 'ApolloClientBadUserInputError'
  }
}

class ApolloClientUnauthenticatedError extends Error {
  // eslint-disable-next-line unicorn/custom-error-definition
  public constructor(message: string) {
    super(message)
    this.name = 'ApolloClientUnauthenticatedError'
  }
}

class ApolloClientForbiddenError extends Error {
  // eslint-disable-next-line unicorn/custom-error-definition
  public constructor(message: string) {
    super(message)
    this.name = 'ApolloClientForbiddenError'
  }
}

class ApolloClientInternalServerErrorError extends Error {
  // eslint-disable-next-line unicorn/custom-error-definition
  public constructor(message: string) {
    super(message)
    this.name = 'ApolloClientInternalServerErrorError'
  }
}

//
// 前提として、サーバー側でも正しくエラー通知が行われており同等の通知が飛んでいるが、
// より確実にエラーを検知するため、クライアントはクライアントで丁寧にハンドリングして通知を行っておく
//
function notifyGraphQLError(error: GraphQLErrors[0], operation: Operation): void {
  const code = error.extensions?.code
  const isKnownAuthCode =
    code === 'BAD_USER_INPUT' ||
    code === 'UNAUTHENTICATED' ||
    code === 'FORBIDDEN' ||
    code === 'USER_ACCOUNT_ALREADY_EXISTS' ||
    code === 'VISUALIZE_LICENSE_REQUIRED'
  // 既知の認可系 code でサーバーが bugsnagIgnore を明示しているときだけ完全抑止。
  // code 不明の bugsnagIgnore は backstop として internal server error 通知に流す (= 想定外サーバ regression を検出するため)。
  if (isKnownAuthCode && error.extensions?.bugsnagIgnore === true) {
    return
  }

  const metadata = getMetadata(operation)

  switch (code) {
    case 'BAD_USER_INPUT': {
      notifyBugsnag({
        error: new ApolloClientBadUserInputError(error.message),
        metadata,
        severity: 'info',
      })
      return
    }
    case 'UNAUTHENTICATED': {
      notifyBugsnag({
        error: new ApolloClientUnauthenticatedError(error.message),
        metadata,
        severity: 'info',
      })
      return
    }
    case 'FORBIDDEN':
    case 'VISUALIZE_LICENSE_REQUIRED': {
      notifyBugsnag({
        error: new ApolloClientForbiddenError(error.message),
        metadata,
        severity: 'info',
      })
      return
    }
  }

  notifyBugsnag({
    error: new ApolloClientInternalServerErrorError(error.message),
    metadata,
    severity: 'error',
  })
}

export const errorLink = onError((argument) => {
  // eslint-disable-next-line sonarjs/deprecation
  const { graphQLErrors, networkError, operation } = argument

  if (isNotNullish(graphQLErrors)) {
    for (const error of graphQLErrors) {
      notifyGraphQLError(error, operation)
    }
  }
  if (isNotNullish(networkError)) {
    // httpLinkがfetch失敗時にrequestIdを載せていれば、サーバーログ追跡用にBugSnagへ含める
    const requestId = getRequestId(networkError)
    const baseMetadata = getMetadata(operation)
    notifyBugsnag({
      error: new ApolloClientNetworkError(`${networkError.name}: ${networkError.message}`, { cause: networkError }),
      metadata: {
        key: baseMetadata.key,
        value: {
          ...baseMetadata.value,
          online: navigator.onLine,
          visibilityState: document.visibilityState,
          ...(requestId === undefined ? {} : { requestId }),
        },
      },
    })
    logger.error(LoggerEvents.APOLLO_CLIENT, `${networkError.name}: ${networkError.message}`)
  }
})

function getMetadata(operation: Operation): {
  key: string
  value: { operationName: string; variables: Record<string, unknown> }
} {
  return {
    key: 'graphql',
    value: {
      operationName: operation.operationName,
      variables: operation.variables,
      // query: operation.query, // queryを見るとnodeになっており分かりづらい。文字列のクエリが見たいが、相当する情報が見つからなかった。
    },
  }
}
